import type { AnnouncementModalContent } from '../components/Modals/Announcement/Announcment'
import { api } from '../lib/axios'
import { useCallback, useEffect, useState } from 'react'
// Hook to manage the session modal for announcementss
type NextCampaignResponse = {
  campaign: (AnnouncementModalContent & {
    id: number
    featureId: number
    featureName: string
    dayOfWeek: 'monday' | 'wednesday' | 'friday'
  }) | null
}

const SESSION_STORAGE_KEY = '@whatsmenu:campaign-seen'

const WEBINAR_SUCCESS_MODAL_CONTENT: AnnouncementModalContent = {
  badge: 'Webinar',
  title: 'Inscrição realizada com sucesso!',
  body: 'Você receberá o acesso no dia marcado.',
  ctaLabel: 'Fechar',
}

export function useSessionModal(enabled: boolean, authToken?: string | null) {
  const [campaign, setCampaign] = useState<AnnouncementModalContent | null>(null)
  const [successCampaign, setSuccessCampaign] = useState<AnnouncementModalContent | null>(null)
  const [show, setShow] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [loading, setLoading] = useState(false)

  const markAsSeen = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(SESSION_STORAGE_KEY, 'true')
    }

    setShow(false)
  }, [])

  const closeSuccessModal = useCallback(() => {
    setSuccessCampaign(null)
    setShowSuccess(false)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    if (!authToken) {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    if (typeof window === 'undefined') {
      return
    }

    if (window.sessionStorage.getItem(SESSION_STORAGE_KEY) === 'true') {
      setCampaign(null)
      setShow(false)
      setLoading(false)
      return
    }

    let cancelled = false
    api.defaults.headers.common.Authorization = `Bearer ${authToken}`

    const loadCampaign = async () => {
      setLoading(true)

      try {
        await api.get('/dashboard/features/health-stats')

        const { data } = await api.get<NextCampaignResponse>('/dashboard/features/next-campaign')

        if (cancelled) return

        if (data.campaign) {
          const isWebinarCampaign = data.campaign.featureName.trim().toLowerCase() === 'webinar'

          const campaignContent: AnnouncementModalContent = {
            badge: data.campaign.badge,
            title: data.campaign.title,
            body: data.campaign.body,
            imageUrl: data.campaign.imageUrl ?? null,
            ctaLabel: data.campaign.ctaLabel,
            ctaUrl: data.campaign.ctaUrl ?? null,
            learnMoreUrl: data.campaign.learnMoreUrl ?? null,
            learnMoreLabel: data.campaign.learnMoreLabel ?? undefined,
          }

          if (isWebinarCampaign) {
            campaignContent.onCtaClick = async () => {
              await api.post('/dashboard/features/webinar/subscribe')
              markAsSeen()
              setSuccessCampaign(WEBINAR_SUCCESS_MODAL_CONTENT)
              setShowSuccess(true)
            }
          }

          setCampaign({
            ...campaignContent,
          })
          setShow(true)
        } else {
          setCampaign(null)
          setShow(false)
        }
      } catch {
        if (!cancelled) {
          setCampaign(null)
          setShow(false)
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    void loadCampaign()

    return () => {
      cancelled = true
    }
  }, [enabled, authToken, markAsSeen])

  return {
    campaign,
    show,
    successCampaign,
    showSuccess,
    loading,
    markAsSeen,
    closeSuccessModal,
  }
}
